import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const FreelancerAccountInfoPage = () => {
  const [accountInfo, setAccountInfo] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const columns = [
    'Freelancer ID',
    'Freelancer Name', // ✅ New Column
    'Account Status',
    'Freeze From',
    'Freeze To',
    'Freeze Reason',
    'Blocked Reason'
  ];

  useEffect(() => {
    const fetchAccountInfo = async () => {
      try {
        const response = await axios.get(`${apiBaseURL}/api/freelancer-account-info`);
        const formattedData = response.data.map(info => ({
          freelancer_id: info.freelancer_id,
          freelancer_name: info.freelancer_name || 'N/A', // ✅ Added
          account_status: info.account_status,
          freeze_from: formatDateToIST(info.freeze_from),
          freeze_to: formatDateToIST(info.freeze_to),
          freeze_reason: info.freeze_reason,
          blocked_reason: info.blocked_reason,
        }));
        setAccountInfo(formattedData);
        setFilteredData(formattedData);
      } catch (err) {
        setError('Failed to fetch freelancer account info.');
      } finally {
        setLoading(false);
      }
    };

    fetchAccountInfo();
  }, [apiBaseURL]);

  const formatDateToIST = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    applyFilters(value, searchQuery);
  };

  const handleSearchQueryChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    applyFilters(statusFilter, value);
  };

  const applyFilters = (status, query) => {
    let filtered = accountInfo;

    if (status) {
      filtered = filtered.filter((entry) => entry.account_status === status);
    }

    if (query) {
      filtered = filtered.filter((entry) =>
        entry.freelancer_id.toString().includes(query)
      );
    }

    setFilteredData(filtered);
  };

  const downloadExcel = () => {
    const dataToExport = filteredData.map((row) => ({
      'Freelancer ID': row.freelancer_id,
      'Freelancer Name': row.freelancer_name, // ✅ Added
      'Account Status': row.account_status,
      'Freeze From': row.freeze_from,
      'Freeze To': row.freeze_to,
      'Freeze Reason': row.freeze_reason,
      'Blocked Reason': row.blocked_reason,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'FreelancerAccountInfo');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const fileData = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(fileData, 'Freelancer_Account_Info.xlsx');
  };

  const Table = ({ columns, data }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto bg-white shadow-md rounded-lg">
        <thead>
          <tr className="bg-gray-200">
            {columns.map((col, index) => (
              <th key={index} className="px-4 py-2 text-left">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-2 text-center text-gray-500">No data available</td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr key={index} className="border-b">
                {columns.map((col, idx) => (
                  <td key={idx} className="px-4 py-2">
                    {row[col.toLowerCase().replace(/ /g, '_')]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <div>Loading account info...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="flex-1 p-6 bg-gray-100 ml-64">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold">
          Freelancer Account Info
          <span className="text-gray-500 text-lg ml-2">
            ({filteredData.length} of {accountInfo.length} Accounts)
          </span>
        </h2>
        <button
          onClick={downloadExcel}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow"
        >
          Download Excel
        </button>
      </div>

      <div className="mb-4 flex justify-between">
        <div>
          <label htmlFor="statusFilter" className="mr-2">Filter by Status: </label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            className="px-4 py-2 border rounded"
          >
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Freezed">Freezed</option>
            <option value="Blocked">Blocked</option>
          </select>
        </div>

        <div>
          <label htmlFor="search" className="mr-2">Search by Freelancer ID: </label>
          <input
            type="text"
            id="search"
            value={searchQuery}
            onChange={handleSearchQueryChange}
            className="px-4 py-2 border rounded"
            placeholder="Enter Freelancer ID"
          />
        </div>
      </div>

      <Table columns={columns} data={filteredData} />
    </div>
  );
};

export default FreelancerAccountInfoPage;
